import { FC, useState, useEffect } from 'react'
import { extractExecFolder as extractExecFolderUtil, extractUserFolder } from '../../utils/tables'
import { collectUserFolders } from '../batch/FolderPickerDialog'
import type { UserTOCEntry } from '../../hooks/useUserTOC'
import MenuItem from '@mui/material/MenuItem'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import TextField from '@mui/material/TextField'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import RadioGroup from '@mui/material/RadioGroup'
import Radio from '@mui/material/Radio'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'

export type SaveDestination = 'user' | 'exec'

export interface SaveTableResult {
  name: string
  subtitle: string
  overwrite: boolean
  destination: SaveDestination
  execFolder: string
  /** The My Tables folder to save into, relative to Tables/User/<email> ('A/B'); '' is the top level. */
  userFolder: string
}

interface SaveTableDialogProps {
  open: boolean
  /** The original table path being edited, e.g. Tables/User/matt@bayesprice.com/Q6.cbt */
  originalPath: string
  /** Current user's email */
  userEmail: string
  onSave: (result: SaveTableResult) => void
  onCancel: () => void
  saving?: boolean
  /** Whether the user has manager role (can save to Exec) */
  isManager?: boolean
  /** Available Exec folder paths for the folder picker */
  execFolders?: string[]
  /** user.toc, for the My Tables folder picker */
  userFolders?: UserTOCEntry[]
}

/** Returns true if the table path belongs to the current user's folder */
function isOwnUserTable(path: string, email: string): boolean {
  if (!path || !email) return false
  return path.toLowerCase().startsWith(`tables/user/${email.toLowerCase()}/`)
}

/** Extracts the table name (without .cbt) from a path */
function extractTableName(path: string): string {
  return path.split('/').pop()?.replace(/\.cbt$/, '') || 'Edited Table'
}

const extractExecFolder = extractExecFolderUtil

const SaveTableDialog: FC<SaveTableDialogProps> = ({
  open,
  originalPath,
  userEmail,
  onSave,
  onCancel,
  saving = false,
  isManager = false,
  execFolders = [],
  userFolders = [],
}) => {
  const isOwn = isOwnUserTable(originalPath, userEmail)
  const isExecTable = originalPath.toLowerCase().startsWith('tables/exec/')
  const originalName = extractTableName(originalPath)
  // The folder the edited table is in: the default, and the only place an
  // overwrite can go (anything else would write a second copy).
  const originalFolder = isOwn ? extractUserFolder(originalPath, userEmail) : ''

  const [name, setName] = useState(originalName)
  const [subtitle, setSubtitle] = useState('')
  const [overwrite, setOverwrite] = useState(isOwn)
  const [destination, setDestination] = useState<SaveDestination>('user')
  const [execFolder, setExecFolder] = useState('')
  const [userFolder, setUserFolder] = useState(originalFolder)

  // Reset state when dialog opens with new table
  useEffect(() => {
    if (open) {
      const ownTable = isOwnUserTable(originalPath, userEmail)
      const tableName = extractTableName(originalPath)
      setName(ownTable ? tableName : tableName)
      setSubtitle('')
      setOverwrite(ownTable)
      setDestination('user')
      setExecFolder(isExecTable ? extractExecFolder(originalPath) : '')
      setUserFolder(ownTable ? extractUserFolder(originalPath, userEmail) : '')
    }
  }, [open, originalPath, userEmail, isExecTable])

  const handleSave = () => {
    if (!name.trim()) return
    onSave({
      name: name.trim(),
      subtitle: subtitle.trim(),
      overwrite: destination === 'user' && overwrite,
      destination,
      execFolder: execFolder.trim(),
      userFolder: destination === 'user' ? (overwrite ? originalFolder : userFolder) : '',
    })
  }

  // When overwrite is toggled off, suggest a new name to avoid conflict
  const handleOverwriteChange = (checked: boolean) => {
    setOverwrite(checked)
    if (checked) {
      setName(originalName)
      setUserFolder(originalFolder)
    } else if (name === originalName) {
      setName(`${originalName} (Edited)`)
    }
  }

  const handleDestinationChange = (dest: SaveDestination) => {
    setDestination(dest)
    if (dest === 'exec') {
      setOverwrite(false)
      if (name.endsWith(' (Edited)')) {
        setName(name.replace(' (Edited)', ''))
      }
    }
  }

  // Folder choices: the top level, every user.toc folder, and the table's
  // own folder even when user.toc does not list it.
  const folderOptions = [
    { path: '', label: 'My Tables (top level)', depth: 0 },
    ...collectUserFolders(userFolders, '', 1),
  ]
  if (originalFolder && !folderOptions.some(f => f.path === originalFolder)) {
    folderOptions.push({ path: originalFolder, label: originalFolder, depth: 1 })
  }

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Save Table</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {isManager && (
            <Box>
              <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>Save to</Typography>
              <RadioGroup
                row
                value={destination}
                onChange={(e) => handleDestinationChange(e.target.value as SaveDestination)}
              >
                <FormControlLabel value="user" control={<Radio size="small" color="info" />} label="My Tables" />
                <FormControlLabel value="exec" control={<Radio size="small" color="info" />} label="Exec (shared)" />
              </RadioGroup>
            </Box>
          )}
          {destination === 'user' && isOwn && (
            <FormControlLabel
              control={
                <Switch
                  checked={overwrite}
                  onChange={(_e, checked) => handleOverwriteChange(checked)}
                  color="info"
                />
              }
              label={
                <Typography variant="body2">
                  Overwrite <strong>{originalName}</strong>
                </Typography>
              }
            />
          )}
          <TextField
            label="Table name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={destination === 'user' && overwrite}
            size="small"
            fullWidth
            autoFocus={!isOwn}
          />
          {destination === 'user' && (
            <TextField
              select
              label="Folder"
              value={overwrite ? originalFolder : userFolder}
              onChange={(e) => setUserFolder(e.target.value)}
              disabled={overwrite}
              size="small"
              fullWidth
              helperText={overwrite ? 'Overwriting keeps the table in its folder' : undefined}
            >
              {folderOptions.map(f => (
                <MenuItem key={f.path || '(top)'} value={f.path} sx={{ pl: 2 + f.depth * 2 }}>
                  {f.label}
                </MenuItem>
              ))}
            </TextField>
          )}
          {destination === 'exec' && (
            <TextField
              label="Exec folder (optional)"
              value={execFolder}
              onChange={(e) => setExecFolder(e.target.value)}
              size="small"
              fullWidth
              placeholder="e.g. Charts, Survey Tables"
              helperText={execFolders.length > 0
                ? `Existing: ${execFolders.join(', ')}`
                : 'Leave empty to save at root of Exec'}
            />
          )}
          <TextField
            label="Subtitle (optional)"
            value={subtitle}
            onChange={(e) => setSubtitle(e.target.value)}
            size="small"
            fullWidth
            placeholder="Shows beneath the table name in the TOC"
            helperText="Displayed as metadata under the table name"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          color="info"
          variant="contained"
          disabled={saving || !name.trim()}
        >
          {saving
            ? 'Saving...'
            : destination === 'exec'
              ? 'Save to Exec'
              : overwrite ? 'Save' : 'Save As New'
          }
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SaveTableDialog
